import { css, Global } from "@emotion/react";
import App from "./App";
import { Provider } from "react-redux";
import { store } from "./store";

import "./setupViewport";
import "./i18n";

import { createRoot } from "react-dom/client";

// When a freshly-deployed service worker takes control (autoUpdate skipWaiting
// + clientsClaim), reload the page once so the new build is shown immediately —
// no manual refresh ever needed. Skips the very first install (no prior
// controller) and guards against reload loops.
if ("serviceWorker" in navigator) {
  const hadController = !!navigator.serviceWorker.controller;
  let refreshing = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing || !hadController) return;
    refreshing = true;
    window.location.reload();
  });
}

const globalStyles = css`
  :root {
    // https://stackoverflow.com/a/57708812
    --sat: env(safe-area-inset-top);
    --sar: env(safe-area-inset-right);
    --sab: env(safe-area-inset-bottom);
    --sal: env(safe-area-inset-left);

    // Paramoteur identity tokens (source: paramoteur.treevolta.com)
    --amber: #ff7a1a;
    --amber-2: #ffb13d;
    --ember: linear-gradient(135deg, #ffb13d 0%, #ff7a1a 45%, #ff4d1a 100%);
  }

  html {
    // Color for Safari overscroll
    // Has to be a solid color... because Safari
    background-color: var(--overscroll-background);

    -webkit-text-size-adjust: 100%;
  }

  body {
    margin: 0;
    padding: env(safe-area-inset-top) 0 env(safe-area-inset-bottom);
    font-family:
      "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto",
      "Helvetica Neue", sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;

    position: relative; // needed for AsideHouses
    display: flex;
    flex-direction: column;
    min-height: 100vh; // AsideHouses, and keep footer at bottom of viewport

    color: var(--text);
    line-height: 1.4;

    box-sizing: border-box;

    @media (any-hover: none) {
      user-select: none;
    }

    @media (display-mode: standalone) {
      -webkit-tap-highlight-color: transparent;
    }
  }

  .leaflet-container {
    background: rgba(255, 255, 255, 0.05) !important;
  }

  *:focus {
    outline: none;
  }

  *,
  *:before,
  *:after {
    box-sizing: inherit;
  }

  #root {
    flex: 1;

    display: flex;
    flex-direction: column;
  }

  a {
    color: inherit;

    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  // Paramoteur display type — Sora headlines over Inter body.
  h1,
  h2,
  h3,
  h4 {
    font-family: "Sora", "Inter", sans-serif;
    letter-spacing: -0.01em;
  }
`;

const rootNode = document.getElementById("root");
const root = createRoot(rootNode!);
root.render(
  <>
    <Global styles={globalStyles} />
    <Provider store={store}>
      <App />
    </Provider>
  </>,
);
